'use client';

import React from 'react';
import BigNumber from 'bignumber.js';
import { BsCircleFill } from 'react-icons/bs';
import { MdOutlineSearchOff } from 'react-icons/md';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

import useQueryWithPages from 'src/hooks/useQueryWithPages';

import { formatTimestamp } from '../../utils/formatTimestamp';
import styles from './InternalTransactionsPage.module.css';

const InternalTransactionsPage: React.FC = () => {
  const query = useQueryWithPages({
    resourceName: 'internal_txs',
  });

  const transactions = React.useMemo(() => {
    return query.data?.items || [];
  }, [query.data?.items]);

  const formatValue = (value: string | null | undefined) => {
    if (!value || value === '0') return '0';
    const bn = BigNumber(value);
    const eth = bn.dividedBy(BigNumber(10).pow(18));
    if (eth.isGreaterThanOrEqualTo(1000)) {
      return `${eth.dividedBy(1000).toFixed(2)}K`;
    }
    return eth.toFixed(6);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Internal transactions</h1>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            disabled={!query.pagination.canGoBackwards}
            onClick={query.pagination.onPrevPageClick}
          >
            &lt; Prev
          </button>
          <button className={`${styles.pageButton} ${styles.pageButtonActive}`}>
            {query.pagination.page}
          </button>
          <button
            className={styles.pageButton}
            disabled={!query.pagination.hasNextPage}
            onClick={query.pagination.onNextPageClick}
          >
            Next &gt;
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Parent txn hash</th>
              <th>Type</th>
              <th>Block</th>
              <th>From</th>
              <th>To</th>
              <th>Value OGPU</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div className={styles.skeleton} style={{ width: '7rem', height: '0.875rem', display: 'block' }} />
                      <div className={styles.skeleton} style={{ width: '4rem', height: '0.75rem', display: 'block' }} />
                    </div>
                  </td>
                  <td><div className={styles.skeleton} style={{ width: '3.5rem', height: '1.25rem', borderRadius: '0.25rem' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '3.5rem', height: '0.875rem' }} /></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <div className={styles.skeleton} style={{ width: '0.75rem', height: '0.75rem', borderRadius: '50%', flexShrink: 0 }} />
                      <div className={styles.skeleton} style={{ width: '6rem', height: '0.875rem' }} />
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <div className={styles.skeleton} style={{ width: '0.75rem', height: '0.75rem', borderRadius: '50%', flexShrink: 0 }} />
                      <div className={styles.skeleton} style={{ width: '6rem', height: '0.875rem' }} />
                    </div>
                  </td>
                  <td><div className={styles.skeleton} style={{ width: '4rem', height: '0.875rem' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '1rem', height: '1rem', borderRadius: '50%' }} /></td>
                </tr>
              ))
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className={styles.emptyState}>
                    <MdOutlineSearchOff size={48} />
                    <p>There are no internal transactions.</p>
                  </div>
                </td>
              </tr>
            ) : (
              transactions.map((tx: any, index: number) => {
                const toAddress = tx.to || tx.created_contract;
                return (
                  <tr key={`${tx.transaction_hash}-${tx.index}-${index}`}>
                    <td>
                      <a href={`/tx/${tx.transaction_hash}`} className={styles.link}>
                        {tx.transaction_hash?.slice(0, 10)}...{tx.transaction_hash?.slice(-4)}
                      </a>
                      <div className={styles.age}>{formatTimestamp(tx.timestamp)}</div>
                    </td>
                    <td>
                      <span className={styles.typeBadge}>{tx.type}</span>
                    </td>
                    <td>
                      <a href={`/block/${tx.block_number}`} className={styles.link}>
                        {tx.block_number}
                      </a>
                    </td>
                    <td>
                      <div className={styles.addressRow}>
                        <BsCircleFill size={12} color="#3b82f6" />
                        <a href={`/address/${tx.from?.hash}`} className={styles.address}>
                          {tx.from?.hash?.slice(0, 8)}...{tx.from?.hash?.slice(-4)}
                        </a>
                      </div>
                    </td>
                    <td>
                      <div className={styles.addressRow}>
                        <BsCircleFill size={12} color="#8b5cf6" />
                        <a href={`/address/${toAddress?.hash}`} className={styles.address}>
                          {toAddress?.hash?.slice(0, 8)}...{toAddress?.hash?.slice(-4)}
                        </a>
                        {tx.created_contract && (
                          <span className={styles.contractBadge}>Created</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={styles.value}>{formatValue(tx.value)} OGPU</span>
                    </td>
                    <td>
                      {tx.success ? (
                        <FaCheckCircle size={16} color="#22c55e" />
                      ) : (
                        <div className={styles.errorStatus}>
                          <FaTimesCircle size={16} color="#ef4444" />
                          {tx.error && <span className={styles.errorText}>{tx.error}</span>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InternalTransactionsPage;
